import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getAssessmentInfo, putAssessmentInfo, resetGetAssessmentInfo } from '../../redux/actions/assessmentInfo/assessmentInfo';
import { SaveDataState } from '../../redux/reducers/getSaveData/saveDataReducer';
import { SaveResultState } from '../../redux/reducers/getSaveData/saveResultReducer';
import { GrainElevatorAssessmentInfoState } from '../../types/assessment_info/GrainElevatorAssessmentInfoState';
import { BusinessOperationsData } from '../../types/assessment_info/gev/BusinessOperationsData';
import { OperationProps } from '../../types/assessment_info/gev/BusinessOperationFormProps';
import { useBusinessOperationsState } from '../../utils/context/BusinessOperationsContext';
import { useFormErrorContext } from '../../utils/context/FormErrorContext';
import { useNavigation } from '../../utils/context/NavigationContext';
import Overview from '../../components/gev/Overview';
import CommercialGrainElevatorFacility from '../../components/gev/CommercialGrainElevatorFacility';
import FeedMillingProcessingFacility from '../../components/gev/FeedMillingProcessingFacility';
import OtherAgriBusiness from '../../components/gev/OtherAgriBusiness';
import FarmingOperationCommercialGrain from '../../components/gev/FarmingOperationCommercialGrain';
import NoFormSelected from '../../components/gev/NoFormSelected';
import "./BusinessOperations.css";
import { selectGetGrainElevatorAssessmentInfo, selectSaveData, selectSaveResult } from '../../redux/selectors/exportSelectorPaths';

export type BusinessOperationsKeys = keyof BusinessOperationsData;

export const farmingOption = 'farmingOperationCommercialGrain'
export const noFormSelectedOption = 'noFormSelected'
export const allOtherOptions = [
    'commercialGrainElevatorFacility',
    'feedMillingProcessingFacility',
    'otherAgriBusiness'
]
export const orderedBusinessOperations = [farmingOption, ...allOtherOptions, noFormSelectedOption]
export const radioSelectBusinessOperations = [farmingOption, noFormSelectedOption]


export default function BusinessOperations(): JSX.Element {
    const dispatch = useDispatch()
    const { direction } = useNavigation();
    const isInternalUser = sessionStorage.getItem("isInternalUser") === "true"
    const triggerSpectral = isInternalUser || direction !== "backward"
    const [triggerValidation, setTriggerValidation] = useState(triggerSpectral); // conditionally switch off spectral validation
    const getAssessmentInfoState: GrainElevatorAssessmentInfoState = useSelector(selectGetGrainElevatorAssessmentInfo)
    const saveData: SaveDataState = useSelector(selectSaveData)
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const { clearErrors } = useFormErrorContext();
    const { formData, setFormData } = useBusinessOperationsState();
    const [errorGrp, setErrorGrp] = useState<Record<BusinessOperationsKeys, number>>({
        commercialGrainElevatorFacility: 0,
        farmingOperationCommercialGrain: 0,
        feedMillingProcessingFacility: 0,
        otherAgriBusiness: 0,
        noFormSelected: 0
    })
    const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string
    const [isUpdateReqSent, setIsUpdateReqSent] = useState(false);

    const handleTriggerValidation = (newValue: boolean) => {
        setTriggerValidation(newValue);
    }

    useEffect(() => {
        // enable spectral validation if the user is internal, or the user is external and is navigating forward
        (isInternalUser || direction === "forward") ? setTriggerValidation(true) : setTriggerValidation(false)
    }, [direction, isInternalUser]);

    useEffect(() => {
        if (!getAssessmentInfoState.loading && !getAssessmentInfoState.success) {
            dispatch(getAssessmentInfo(currentSnapshotId, "GEV"))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!getAssessmentInfoState.loading) {
            if (getAssessmentInfoState.success) {
                setFormData(getAssessmentInfoState.formData as BusinessOperationsData)
            } else {
                if (getAssessmentInfoState.message) {
                    toast.error(getAssessmentInfoState.message)
                }
            }
            dispatch(resetGetAssessmentInfo());
        }
    }, [getAssessmentInfoState, dispatch, setFormData, saveResultState.status]);

    useEffect(() => {
        if (isUpdateReqSent && !saveResultState.loading) {
            if (saveResultState.status === 200) {
                dispatch(getAssessmentInfo(currentSnapshotId, "GEV"));
            }
            else {
                // toast.error('Failed to save data. Please fix errors and try again');
            }
            setIsUpdateReqSent(false);
            setTriggerValidation(triggerSpectral); // once save is complete, reset spectral validation flag to true
        }
    }, [dispatch, setFormData, currentSnapshotId, saveResultState, isUpdateReqSent, triggerSpectral]);


    useEffect(() => {
        // Trigger save only if context changed
        if (saveData.saving && (formData !== getAssessmentInfoState.formData)) {
            clearErrors();
            setErrorGrp({
                commercialGrainElevatorFacility: 0,
                farmingOperationCommercialGrain: 0,
                feedMillingProcessingFacility: 0,
                otherAgriBusiness: 0,
                noFormSelected: 0
            })
            dispatch(putAssessmentInfo(currentSnapshotId, {
                "data": formData,
                "trigger_spectral": triggerValidation,
                "is_internal": sessionStorage.getItem("isInternalUser") as string
            }));
            setIsUpdateReqSent(true);
        }
    }, [dispatch, saveData.saving, triggerValidation, currentSnapshotId, formData, getAssessmentInfoState.formData, clearErrors, direction]);


    const displayOperation = (operationKey: BusinessOperationsKeys) => {
        const props: OperationProps = {
            operation: operationKey,
            incrementErrCount: incrementErrCount,
            resetErrCount: resetErrCount,
            // validating: validating,
            // validate: validate,
            errors: {},
            // setErrors: setOperationErrors,
        }
        switch (operationKey) {
            case "commercialGrainElevatorFacility":
                return <CommercialGrainElevatorFacility {...props} />
            case "feedMillingProcessingFacility":
                return <FeedMillingProcessingFacility {...props} />
            case "otherAgriBusiness":
                return <OtherAgriBusiness {...props} />
            case "farmingOperationCommercialGrain":
                return <FarmingOperationCommercialGrain {...props} />
            case "noFormSelected":
                return <NoFormSelected {...props} />
            default:
                return null
        }
    }

    const incrementErrCount = (operationKey: BusinessOperationsKeys) => {
        const newCount = errorGrp[operationKey] + 1;
        setErrorGrp(prevData => ({
            ...prevData,
            [operationKey]: newCount
        }));
    }

    const resetErrCount = (operationKey: BusinessOperationsKeys) => {
        setErrorGrp(prevData => ({
            ...prevData,
            [operationKey]: 0
        }));
    }

    return (
        <div className='gev-width'>
            <Overview resetErrCount={resetErrCount} handleTriggerValidation={handleTriggerValidation} />
            <div className='form-container'>
                {
                    Object.keys(formData).length > 0
                    && Object.keys(formData).map((key) => {
                        return (
                            (formData[key as BusinessOperationsKeys].selected) &&
                            <>{displayOperation(key as BusinessOperationsKeys)}</>
                        )
                    })
                }

            </div>
        </div>

    )
}
